import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import useAutoRetry from "../hooks/useAutoRetry";
import repository from "./repository";
import { cacheBlocks, getCachedBlocks } from "./contentCache";
import { DENIED, RETRYABLE, problemOf } from "./problems";

const useLessonBlocks = (lessonId) => {
  const { user } = useAuth();
  const userId = user.id;
  const [attempt, setAttempt] = useState(0);
  // Automatic retries keep the current screen instead of flashing loading.
  const quiet = useRef(false);
  const [state, setState] = useState(() => {
    const cached = lessonId && getCachedBlocks(userId, lessonId);
    return cached
      ? { status: "ready", blocks: cached, problem: null }
      : { status: "loading", blocks: [], problem: null };
  });

  useEffect(() => {
    if (!lessonId) return undefined;
    let active = true;
    const cached = getCachedBlocks(userId, lessonId);
    if (!quiet.current) {
      setState(
        cached
          ? { status: "ready", blocks: cached, problem: null }
          : { status: "loading", blocks: [], problem: null }
      );
    }
    quiet.current = false;

    repository.listBlocks(lessonId).then(
      (blocks) => {
        if (!active) return;
        cacheBlocks(userId, lessonId, blocks);
        setState({ status: "ready", blocks, problem: null });
      },
      (error) => {
        if (!active) return;
        const problem = problemOf(error);
        setState(
          cached && problem !== DENIED
            ? { status: "ready", blocks: cached, problem }
            : { status: "error", blocks: [], problem }
        );
      }
    );

    return () => {
      active = false;
    };
  }, [userId, lessonId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const retryQuietly = useCallback(() => {
    quiet.current = true;
    setAttempt((n) => n + 1);
  }, []);

  const autoRetrying = Boolean(lessonId) && RETRYABLE.has(state.problem);
  useAutoRetry(autoRetrying, retryQuietly);

  return { ...state, retry, autoRetrying };
};

export default useLessonBlocks;
